
import { useState, useCallback } from "react";
import { toast } from "sonner";
import i18n from "@/i18n";

interface UseAttendanceTableProps {
  onRemoveClient?: (classId: string, userId: string, classDate: string, userName: string) => Promise<boolean>;
  onMarkAttendance?: (bookingId: string, attended: boolean, notes?: string) => Promise<boolean>;
  onRefreshData?: () => Promise<void>;
}

export const useAttendanceTable = ({
  onRemoveClient,
  onMarkAttendance,
  onRefreshData
}: UseAttendanceTableProps) => {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [removingClientId, setRemovingClientId] = useState<string | null>(null);
  const [updatingAttendanceId, setUpdatingAttendanceId] = useState<string | null>(null);

  const toggleExpandRow = useCallback((bookingId: string) => {
    if (expandedRow === bookingId) {
      setExpandedRow(null);
    } else {
      setExpandedRow(bookingId);
    }
  }, [expandedRow]);

  const handleRemoveClient = useCallback(async (
    classId: string, 
    userId: string, 
    date: string, 
    userName: string
  ): Promise<boolean> => {
    if (!onRemoveClient) return false;
    
    setRemovingClientId(userId);
    try {
      const success = await onRemoveClient(classId, userId, date, userName);
      if (success) {
        toast.success(i18n.t("clientRemovedFromClass", { ns: "common", name: userName }));
        if (onRefreshData) await onRefreshData();
      }
      return success;
    } catch (error) {
      console.error("Error removing client:", error);
      toast.error(i18n.t("clientRemoveError", { ns: "common" }));
      return false;
    } finally {
      setRemovingClientId(null);
    }
  }, [onRemoveClient, onRefreshData]);

  const handleMarkAttendance = useCallback(async (
    bookingId: string, 
    attended: boolean, 
    userName: string
  ): Promise<boolean> => {
    if (!onMarkAttendance) return false;
    
    setUpdatingAttendanceId(bookingId);
    try {
      const success = await onMarkAttendance(bookingId, attended);
      if (success) {
        toast.success(i18n.t(attended ? "attendanceMarkedPresent" : "attendanceMarkedAbsent", { ns: "common", name: userName }));
        if (onRefreshData) await onRefreshData();
      }
      return success;
    } catch (error) {
      console.error("Error marking attendance:", error);
      toast.error(i18n.t("attendanceUpdateError", { ns: "common" }));
      return false;
    } finally {
      setUpdatingAttendanceId(null);
    }
  }, [onMarkAttendance, onRefreshData]);

  return {
    expandedRow,
    removingClientId,
    updatingAttendanceId,
    toggleExpandRow,
    handleRemoveClient,
    handleMarkAttendance
  };
};
